import React, { useState, useEffect } from 'react';
import { auth, db } from '../firebase';
import { doc, setDoc, getDoc, serverTimestamp } from 'firebase/firestore';
import { loadLatestMetrics, saveMetrics } from '../db';
import { Icon } from './Icons';

// ── PHQ-9 Questions ────────────────────────────────────────────────────────────
const PHQ9_QUESTIONS = [
  'Little interest or pleasure in doing things',
  'Feeling down, depressed, or hopeless',
  'Trouble falling or staying asleep, or sleeping too much',
  'Feeling tired or having little energy',
  'Poor appetite or overeating',
  'Feeling bad about yourself — or that you are a failure or have let yourself or your family down',
  'Trouble concentrating on things, such as reading the newspaper or watching television',
  'Moving or speaking so slowly that other people could have noticed. Or the opposite — being so fidgety or restless that you have been moving around a lot more than usual',
  'Thoughts that you would be better off dead, or of hurting yourself in some way',
];

// ── GAD-7 Questions ────────────────────────────────────────────────────────────
const GAD7_QUESTIONS = [
  'Feeling nervous, anxious, or on edge',
  'Not being able to stop or control worrying',
  'Worrying too much about different things',
  'Trouble relaxing',
  'Being so restless that it is hard to sit still',
  'Becoming easily annoyed or irritable',
  'Feeling afraid, as if something awful might happen',
];

// ── Epworth Questions ──────────────────────────────────────────────────────────
const EPWORTH_QUESTIONS = [
  'Sitting and reading',
  'Watching TV',
  'Sitting inactive in a public place (e.g. theatre or meeting)',
  'As a passenger in a car for an hour without a break',
  'Lying down to rest in the afternoon when circumstances permit',
  'Sitting and talking to someone',
  'Sitting quietly after a lunch without alcohol',
  'In a car, while stopped for a few minutes in traffic',
];

const FREQ_OPTIONS = [
  { value: 0, label: 'Not at all' },
  { value: 1, label: 'Several days' },
  { value: 2, label: 'More than half the days' },
  { value: 3, label: 'Nearly every day' },
];

const EPWORTH_OPTIONS = [
  { value: 0, label: 'Would never doze' },
  { value: 1, label: 'Slight chance' },
  { value: 2, label: 'Moderate chance' },
  { value: 3, label: 'High chance' },
];

function phq9Severity(score) {
  if (score < 5)  return { label: 'Minimal', color: 'var(--success)', desc: 'Your responses suggest minimal depressive symptoms. Continue to monitor your mood over time.' };
  if (score < 10) return { label: 'Mild', color: 'var(--warning)', desc: 'Your responses suggest mild depressive symptoms. Consider talking to your GP if symptoms persist for more than 2 weeks.' };
  if (score < 15) return { label: 'Moderate', color: 'var(--warning)', desc: 'Your responses suggest moderate depression. We recommend speaking to your GP or a mental health professional.' };
  if (score < 20) return { label: 'Moderately severe', color: 'var(--danger)', desc: 'Your responses suggest moderately severe depression. Please speak to your GP as soon as possible.' };
  return { label: 'Severe', color: 'var(--danger)', desc: 'Your responses suggest severe depression. Please contact your GP or call 116 123 (Samaritans) if you need immediate support.' };
}

function gad7Severity(score) {
  if (score < 5)  return { label: 'Minimal', color: 'var(--success)', desc: 'Your responses suggest minimal anxiety symptoms.' };
  if (score < 10) return { label: 'Mild', color: 'var(--warning)', desc: 'Your responses suggest mild anxiety. Relaxation techniques and regular exercise can help.' };
  if (score < 15) return { label: 'Moderate', color: 'var(--warning)', desc: 'Your responses suggest moderate anxiety. Consider speaking to your GP.' };
  return { label: 'Severe', color: 'var(--danger)', desc: 'Your responses suggest severe anxiety. Please speak to your GP as soon as possible.' };
}

function epworthSeverity(score) {
  if (score < 8)  return { label: 'Normal', color: 'var(--success)', desc: 'Your sleepiness level is within the normal range.' };
  if (score < 11) return { label: 'Mild sleepiness', color: 'var(--warning)', desc: 'You have mild daytime sleepiness. Improving sleep hygiene may help.' };
  if (score < 16) return { label: 'Moderate sleepiness', color: 'var(--warning)', desc: 'Moderate daytime sleepiness. Consider speaking to your GP — this can indicate a sleep disorder.' };
  return { label: 'Severe sleepiness', color: 'var(--danger)', desc: 'Severe daytime sleepiness. Please speak to your GP. This may indicate obstructive sleep apnoea or another sleep disorder.' };
}

// ── Single Assessment Component ────────────────────────────────────────────────
function Assessment({ title, subtitle, icon, questions, options, maxScore, severityFn, savedScore, savedDate, onComplete, preamble, crisis }) {
  const [answers, setAnswers] = useState(Array(questions.length).fill(null));
  const [submitted, setSubmitted] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);

  const allAnswered = answers.every(a => a !== null);
  const score = answers.reduce((s, a) => s + (a || 0), 0);
  const severity = submitted ? severityFn(score) : null;

  async function handleSubmit() {
    if (!allAnswered) return;
    setSaving(true);
    try {
      const fieldScore = title.includes('PHQ') ? 'phq9' : title.includes('GAD') ? 'gad7' : 'epworth';
      await saveMetrics('mental', { [fieldScore]: score, [fieldScore + 'Date']: new Date().toISOString() });
      setSubmitted(true);
      setShowForm(false);
      if (onComplete) onComplete(fieldScore, score);
    } catch(e) { console.error(e); }
    setSaving(false);
  }

  return (
    <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border2)', borderRadius: 'var(--radius-lg)', overflow: 'hidden', marginBottom: '1rem' }}>
      {/* Header */}
      <div style={{ padding: '1.25rem 1.5rem', display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
        <div style={{ width: 44, height: 44, borderRadius: 10, background: 'rgba(0,174,239,0.08)', border: '1px solid rgba(0,174,239,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <Icon name={icon} size={20} color="var(--primary)" />
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: '.95rem', fontWeight: 600, color: 'var(--text)', marginBottom: '.15rem' }}>{title}</div>
          <div style={{ fontSize: '.75rem', color: 'var(--text3)', fontWeight: 300 }}>{subtitle} · {questions.length} questions · ~2 mins</div>
        </div>
        {(savedScore !== null && savedScore !== undefined) && !showForm && (
          <div style={{ textAlign: 'right', flexShrink: 0 }}>
            {(() => {
              const sev = severityFn(parseFloat(savedScore));
              return (
                <>
                  <div style={{ fontSize: '1.1rem', fontWeight: 700, color: sev.color }}>{savedScore}<span style={{ fontSize: '.7rem', color: 'var(--text3)', fontWeight: 300 }}>/{maxScore}</span></div>
                  <div style={{ fontSize: '.68rem', fontWeight: 600, color: sev.color }}>{sev.label}</div>
                  {savedDate && <div style={{ fontSize: '.62rem', color: 'var(--text3)', marginTop: '.1rem' }}>{new Date(savedDate).toLocaleDateString('en-GB')}</div>}
                </>
              );
            })()}
          </div>
        )}
        <button onClick={() => { setShowForm(v => !v); setSubmitted(false); setAnswers(Array(questions.length).fill(null)); }}
          style={{ padding: '.5rem 1.1rem', borderRadius: 'var(--radius)', border: '1px solid var(--border2)', background: showForm ? 'var(--bg-elevated)' : 'var(--primary)', color: showForm ? 'var(--text3)' : '#080f18', fontFamily: 'var(--font-body)', fontSize: '.75rem', fontWeight: 500, cursor: 'pointer', flexShrink: 0 }}>
          {showForm ? 'Cancel' : savedScore !== null && savedScore !== undefined ? 'Retake' : 'Start Assessment'}
        </button>
      </div>

      {/* Result after submission */}
      {submitted && severity && (
        <div style={{ margin: '0 1.5rem 1.25rem', padding: '1rem 1.25rem', background: severity.color + '10', border: '1px solid ' + severity.color + '30', borderRadius: 'var(--radius)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '.75rem', marginBottom: '.5rem' }}>
            <div style={{ fontSize: '1.5rem', fontWeight: 700, color: severity.color }}>{score}<span style={{ fontSize: '.85rem', color: 'var(--text3)', fontWeight: 300 }}>/{maxScore}</span></div>
            <div>
              <div style={{ fontSize: '.85rem', fontWeight: 600, color: severity.color }}>{severity.label}</div>
              <div style={{ fontSize: '.7rem', color: 'var(--text3)', fontWeight: 300 }}>Saved to your profile</div>
            </div>
          </div>
          <p style={{ fontSize: '.78rem', color: 'var(--text2)', lineHeight: 1.6, fontWeight: 300 }}>{severity.desc}</p>
          {crisis && score >= crisis.threshold && (
            <div style={{ marginTop: '.75rem', padding: '.65rem .85rem', background: 'var(--danger-dim)', border: '1px solid rgba(220,38,38,0.3)', borderRadius: 'var(--radius)', fontSize: '.75rem', color: 'var(--danger)', lineHeight: 1.6 }}>
              <strong>Support available:</strong> {crisis.text}
            </div>
          )}
        </div>
      )}

      {/* Form */}
      {showForm && (
        <div style={{ padding: '0 1.5rem 1.5rem' }}>
          {preamble && (
            <div style={{ padding: '.75rem 1rem', background: 'rgba(0,174,239,0.04)', border: '1px solid rgba(0,174,239,0.12)', borderRadius: 'var(--radius)', fontSize: '.75rem', color: 'var(--text3)', lineHeight: 1.6, marginBottom: '1.25rem', fontWeight: 300 }}>
              {preamble}
            </div>
          )}
          <div style={{ fontSize: '.72rem', color: 'var(--text3)', marginBottom: '1.1rem', fontWeight: 300 }}>
            Over the <strong style={{ color: 'var(--text)' }}>last 2 weeks</strong>, how often have you been bothered by the following?
          </div>

          {/* Option headers */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr repeat(4, 80px)', gap: '.5rem', marginBottom: '.5rem', paddingBottom: '.5rem', borderBottom: '1px solid var(--border2)' }}>
            <div />
            {options.map(o => (
              <div key={o.value} style={{ fontSize: '.62rem', color: 'var(--text3)', textAlign: 'center', lineHeight: 1.3, fontWeight: 500 }}>{o.label}</div>
            ))}
          </div>

          {questions.map((q, qi) => (
            <div key={qi} style={{ display: 'grid', gridTemplateColumns: '1fr repeat(4, 60px)', gap: '.4rem', alignItems: 'center', padding: '.65rem 0', borderBottom: '1px solid var(--border2)' }}>
              <div style={{ fontSize: '.8rem', color: 'var(--text2)', lineHeight: 1.5, paddingRight: '.5rem' }}>
                <span style={{ color: 'var(--text3)', marginRight: '.4rem', fontSize: '.7rem' }}>{qi + 1}.</span>{q}
              </div>
              {options.map(o => (
                <div key={o.value} style={{ display: 'flex', justifyContent: 'center' }}>
                  <button onClick={() => setAnswers(prev => prev.map((a, i) => i === qi ? o.value : a))}
                    style={{ width: 26, height: 26, borderRadius: '50%', border: '2px solid', borderColor: answers[qi] === o.value ? 'var(--primary)' : 'var(--border2)', background: answers[qi] === o.value ? 'var(--primary)' : 'transparent', cursor: 'pointer', transition: 'all .15s', flexShrink: 0 }}>
                    {answers[qi] === o.value && <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#080f18', margin: 'auto' }} />}
                  </button>
                </div>
              ))}
            </div>
          ))}

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '1.25rem', flexWrap: 'wrap', gap: '.75rem' }}>
            <div style={{ fontSize: '.75rem', color: 'var(--text3)', fontWeight: 300 }}>
              {answers.filter(a => a !== null).length} of {questions.length} answered
              {allAnswered && <span style={{ color: 'var(--success)', marginLeft: '.5rem' }}>· Score: {score}/{maxScore}</span>}
            </div>
            <button onClick={handleSubmit} disabled={!allAnswered || saving}
              style={{ padding: '.65rem 1.5rem', borderRadius: 'var(--radius)', border: 'none', background: allAnswered ? 'var(--primary)' : 'var(--bg-elevated)', color: allAnswered ? '#080f18' : 'var(--text3)', fontFamily: 'var(--font-body)', fontSize: '.82rem', fontWeight: 500, cursor: allAnswered && !saving ? 'pointer' : 'not-allowed', opacity: saving ? .7 : 1, display: 'flex', alignItems: 'center', gap: '.4rem' }}>
              <Icon name="check-circle" size={14} color={allAnswered ? '#080f18' : 'var(--text3)'} />
              {saving ? 'Saving…' : 'Submit & Save'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main Export ────────────────────────────────────────────────────────────────
export default function HealthAssessments() {
  const [mental, setMental] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      try {

        const m = await loadLatestMetrics('mental');
        if (m) setMental(m);
      } catch(e) { console.error(e); }
      setLoading(false);
    }
    load();
  }, []);

  function handleComplete(field, score) {
    setMental(prev => ({ ...prev, [field]: score, [field + 'Date']: new Date().toISOString() }));
  }

  if (loading) return <div style={{ padding: '2rem', color: 'var(--text3)', fontSize: '.82rem' }}>Loading assessments…</div>;

  return (
    <div style={{ maxWidth: 860, margin: '0 auto' }}>
      <div style={{ marginBottom: '1.5rem' }}>
        <h2 style={{ fontFamily: 'var(--font-head)', fontSize: '1.4rem', fontWeight: 500, color: 'var(--text)', marginBottom: '.3rem' }}>Health Assessments</h2>
        <p style={{ fontSize: '.78rem', color: 'var(--text3)', fontWeight: 300, lineHeight: 1.6 }}>
          Clinically validated questionnaires. Results are saved to your profile and used by Rico to give you more accurate, contextual health advice.
        </p>
      </div>

      <div style={{ padding: '.75rem 1rem', background: 'rgba(0,174,239,0.04)', border: '1px solid rgba(0,174,239,0.12)', borderRadius: 'var(--radius)', marginBottom: '1.5rem', fontSize: '.75rem', color: 'var(--text3)', lineHeight: 1.6, display: 'flex', gap: '.75rem', alignItems: 'flex-start' }}>
        <Icon name="shield" size={16} color="var(--primary)" style={{ flexShrink: 0, marginTop: 1 }} />
        <span>These are standardised screening tools — they do not constitute a diagnosis. If you are experiencing significant distress, please speak to your GP or call <strong style={{ color: 'var(--text)' }}>116 123</strong> (Samaritans, free, 24/7).</span>
      </div>

      <Assessment
        title="PHQ-9"
        subtitle="Patient Health Questionnaire — Depression screening"
        icon="brain"
        questions={PHQ9_QUESTIONS}
        options={FREQ_OPTIONS}
        maxScore={27}
        severityFn={phq9Severity}
        savedScore={mental.phq9}
        savedDate={mental.phq9Date}
        onComplete={handleComplete}
        preamble="The PHQ-9 is the gold-standard depression screening tool used by GPs worldwide. It takes approximately 2 minutes and asks about common symptoms of depression over the past 2 weeks."
        crisis={{ threshold: 14, text: 'If you are having thoughts of harming yourself, please call 116 123 (Samaritans) or 999 in an emergency. Your GP can also refer you to mental health services.' }}
      />

      <Assessment
        title="GAD-7"
        subtitle="Generalised Anxiety Disorder — Anxiety screening"
        icon="activity"
        questions={GAD7_QUESTIONS}
        options={FREQ_OPTIONS}
        maxScore={21}
        severityFn={gad7Severity}
        savedScore={mental.gad7}
        savedDate={mental.gad7Date}
        onComplete={handleComplete}
        preamble="The GAD-7 is a validated 7-question tool for screening and measuring the severity of generalised anxiety disorder. It is widely used in primary care settings."
      />

      <Assessment
        title="Epworth Sleepiness Scale"
        subtitle="Daytime sleepiness and sleep quality screening"
        icon="moon"
        questions={EPWORTH_QUESTIONS}
        options={EPWORTH_OPTIONS}
        maxScore={24}
        severityFn={epworthSeverity}
        savedScore={mental.epworth}
        savedDate={mental.epworthDate}
        onComplete={handleComplete}
        preamble="The Epworth Sleepiness Scale measures your general level of daytime sleepiness. Rate the chance that you would doze off or fall asleep in the following situations in your normal daily life."
      />

      {/* Combined insight */}
      {(mental.phq9 !== undefined || mental.gad7 !== undefined || mental.epworth !== undefined) && (
        <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border2)', borderRadius: 'var(--radius-lg)', padding: '1.25rem 1.5rem', marginTop: '.5rem' }}>
          <div style={{ fontSize: '.68rem', fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '.1em', marginBottom: '1rem' }}>Your Assessment Summary</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '.85rem' }}>
            {[
              { label: 'PHQ-9', score: mental.phq9, max: 27, fn: phq9Severity, unit: 'Depression' },
              { label: 'GAD-7', score: mental.gad7, max: 21, fn: gad7Severity, unit: 'Anxiety' },
              { label: 'Epworth', score: mental.epworth, max: 24, fn: epworthSeverity, unit: 'Sleepiness' },
            ].map(({ label, score, max, fn, unit }) => {
              if (score === undefined || score === null) return null;
              const sev = fn(parseFloat(score));
              return (
                <div key={label} style={{ background: 'var(--bg-elevated)', borderRadius: 'var(--radius)', padding: '1rem', textAlign: 'center' }}>
                  <div style={{ fontSize: '.68rem', color: 'var(--text3)', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: '.4rem' }}>{label}</div>
                  <div style={{ fontSize: '1.6rem', fontWeight: 700, color: sev.color, lineHeight: 1 }}>{score}</div>
                  <div style={{ fontSize: '.62rem', color: 'var(--text3)', margin: '.2rem 0 .4rem' }}>out of {max}</div>
                  <div style={{ fontSize: '.7rem', fontWeight: 600, color: sev.color, padding: '.2rem .6rem', borderRadius: 20, background: sev.color + '15', display: 'inline-block' }}>{sev.label}</div>
                  <div style={{ fontSize: '.65rem', color: 'var(--text3)', marginTop: '.3rem' }}>{unit}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
